import React, { useCallback, useEffect, useState } from "react";
import MainLayout from "../../components/layout/MainLayout";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./PlanPricingSettings.css";

const formatMoney = (value) =>
  `Rs ${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function PlanPricingSettings() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/super-admin/plans");
      setPlans(
        (response.data?.data || []).map((plan) => ({
          ...plan,
          draft_add_on_rate: String(plan.add_on_rate ?? 0),
        })),
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to load pricing",
        text: error.response?.data?.message || "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const updateDraft = (id, value) => {
    if (value !== "" && !/^\d*(\.\d{0,2})?$/.test(value)) return;
    setPlans((current) =>
      current.map((plan) =>
        plan.id === id ? { ...plan, draft_add_on_rate: value } : plan,
      ),
    );
  };

  const saveRate = async (plan) => {
    const rate = Number(plan.draft_add_on_rate);
    if (!Number.isFinite(rate) || rate < 0) {
      Swal.fire("Invalid price", "Enter a valid add-on employee price.", "warning");
      return;
    }

    setSavingId(plan.id);
    try {
      const response = await api.put(`/super-admin/plans/${plan.id}`, {
        add_on_rate: rate,
      });
      setPlans((current) =>
        current.map((item) =>
          item.id === plan.id
            ? {
                ...item,
                add_on_rate: response.data?.data?.add_on_rate ?? rate,
                draft_add_on_rate: String(response.data?.data?.add_on_rate ?? rate),
              }
            : item,
        ),
      );
      Swal.fire({
        icon: "success",
        title: "Price updated",
        text: "The new rate will apply to future plan activations and upgrades.",
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: error.response?.data?.message || "Please try again.",
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <MainLayout>
      <div className="plan-pricing-page">
        <section className="plan-pricing-hero">
          <div>
            <span className="plan-pricing-eyebrow">Super admin billing</span>
            <h1>Plan Add-on Pricing</h1>
            <p>
              Set the price charged for each active employee above a plan's included seats.
            </p>
          </div>
          <div className="plan-pricing-rule">
            <span>Billing rule</span>
            <strong>Base fee + additional employees x add-on price</strong>
          </div>
        </section>

        {loading ? (
          <div className="plan-pricing-state">Loading plan pricing...</div>
        ) : (
          <section className="plan-pricing-grid">
            {plans.map((plan) => (
              <article className="plan-pricing-card" key={plan.id}>
                <div className="plan-pricing-card-head">
                  <div>
                    <span>{plan.plan_validity || "Plan"}</span>
                    <h2>{plan.plan_name}</h2>
                  </div>
                  <span className={`plan-status ${plan.active_status ? "active" : "inactive"}`}>
                    {plan.active_status ? "Active" : "Inactive"}
                  </span>
                </div>

                <p className="plan-pricing-description">
                  {plan.plan_details || "No plan description available."}
                </p>

                <div className="plan-pricing-facts">
                  <div>
                    <span>Base fee</span>
                    <strong>{formatMoney(plan.base_fee)}</strong>
                  </div>
                  <div>
                    <span>Included employees</span>
                    <strong>{plan.included_employees}</strong>
                  </div>
                </div>

                <label className="plan-rate-field">
                  <span>Add-on price per employee</span>
                  <div>
                    <b>Rs</b>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={plan.draft_add_on_rate}
                      onChange={(event) => updateDraft(plan.id, event.target.value)}
                      aria-label={`${plan.plan_name} add-on employee price`}
                    />
                  </div>
                </label>

                <button
                  type="button"
                  className="plan-rate-save"
                  disabled={savingId === plan.id}
                  onClick={() => saveRate(plan)}
                >
                  {savingId === plan.id ? "Saving..." : "Save Add-on Price"}
                </button>
              </article>
            ))}
          </section>
        )}
      </div>
    </MainLayout>
  );
}

export default PlanPricingSettings;
